import React, { useEffect, useReducer, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ChromePicker } from 'react-color';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import settingsService from '../../services/settings.service';

const colorTemplates = {
  default: {
    name: 'Default Blue',
    primaryColor: '#086cbf',
    secondaryColor: '#b51913',
    backgroundColor: '#dbdbdb',
    utilsColor: '#086cbf',
    navFontColor: '#f7f7f7',
    shadowNavColor: '#086cbf',
    iconsColor: '#ffffff',
    titlesColor: '#ffffff',
    btnFontColor: '#ffffff',
  },
  dark: {
    name: 'Dark Theme',
    primaryColor: '#1a1a1a',
    secondaryColor: '#2d2d2d',
    backgroundColor: '#121212',
    utilsColor: '#333333',
    navFontColor: '#ffffff',
    shadowNavColor: '#000000',
    iconsColor: '#ffffff',
    titlesColor: '#ffffff',
    btnFontColor: '#ffffff',
  },
  nature: {
    name: 'Nature Green',
    primaryColor: '#2e7d32',
    secondaryColor: '#4caf50',
    backgroundColor: '#f1f8e9',
    utilsColor: '#388e3c',
    navFontColor: '#ffffff',
    shadowNavColor: '#1b5e20',
    iconsColor: '#ffffff',
    titlesColor: '#2e7d32',
    btnFontColor: '#ffffff',
  },
  sunset: {
    name: 'Sunset Orange',
    primaryColor: '#ff5722',
    secondaryColor: '#ff9800',
    backgroundColor: '#fff3e0',
    utilsColor: '#f4511e',
    navFontColor: '#ffffff',
    shadowNavColor: '#d84315',
    iconsColor: '#ffffff',
    titlesColor: '#ff5722',
    btnFontColor: '#ffffff',
  },
  custom: {
    name: 'Custom',
  },
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        colors: action.payload,
        primaryColor: action.payload.primaryColor,
        secondaryColor: action.payload.secondaryColor,
        backgroundColor: action.payload.backgroundColor,
        utilsColor: action.payload.utilsColor,
        navFontColor: action.payload.navFontColor,
        shadowNavColor: action.payload.shadowNavColor,
        iconsColor: action.payload.iconsColor,
        titlesColor: action.payload.titlesColor,
        btnFontColor: action.payload.btnFontColor,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };
    default:
      return state;
  }
};

const ColorConfig = () => {
  const { t } = useTranslation();

  const [
    ,
    /* Removed {} */
    // Placeholder for removed state variables
    dispatch,
  ] = useReducer(reducer, {
    // loading: true, // Removed
    // error: '', // Removed
    loadingUpdate: false,
  });

  const [showPicker, setShowPicker] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [colorState, setColorState] = useState({
    primaryColor: '#086cbf',
    secondaryColor: '#b51913',
    backgroundColor: '#dbdbdb',
    utilsColor: '#086cbf',
    navFontColor: '#f7f7f7',
    shadowNavColor: '#086cbf',
    iconsColor: '#ffffff',
    titlesColor: '#ffffff',
    btnFontColor: '#ffffff',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const data = await settingsService.getColors();
        setColorState(data);
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (error) {
        dispatch({ type: 'FETCH_FAIL', payload: error.message });
        toast.error(t('errors.fetchError'));
      }
    };
    fetchData();
  }, [t]);

  const handleColorChange = (color, name) => {
    setColorState(prev => ({
      ...prev,
      [name]: color.hex,
    }));
    setSelectedTemplate('custom');
  };

  const handleTemplateChange = template => {
    if (template === 'custom') {
      return;
    }
    setSelectedTemplate(template);
    setColorState(colorTemplates[template]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      await settingsService.updateColors(colorState);
      toast.success(t('admin.colorsUpdated'));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const colorFields = [
    { name: 'primaryColor', label: t('colors.primary') },
    { name: 'secondaryColor', label: t('colors.secondary') },
    { name: 'backgroundColor', label: t('colors.background') },
    { name: 'utilsColor', label: t('colors.utils') },
    { name: 'navFontColor', label: t('colors.navFont') },
    { name: 'shadowNavColor', label: t('colors.shadowNav') },
    { name: 'iconsColor', label: t('colors.icons') },
    { name: 'titlesColor', label: t('colors.titles') },
    { name: 'btnFontColor', label: t('colors.btnFont') },
  ];

  return (
    <div className='color-config'>
      <form onSubmit={handleSubmit}>
        <div className='mb-4'>
          <h5 className='text-foreground font-medium mb-3'>{t('colors.selectTemplate')}</h5>
          <div className='flex flex-wrap gap-3'>
            {Object.keys(colorTemplates).map(templateKey => (
              <button
                key={templateKey}
                type='button'
                onClick={() => handleTemplateChange(templateKey)}
                className={`overflow-hidden rounded-lg border-2 transition-colors ${
                  selectedTemplate === templateKey ? 'border-primary' : 'border-border'
                } bg-card text-left w-[140px] focus:outline-none focus:ring-2 focus:ring-ring`}
              >
                {templateKey !== 'custom' && (
                  <div
                    className='h-12 w-full'
                    style={{ background: colorTemplates[templateKey].primaryColor }}
                  />
                )}
                <div className='px-3 py-2'>
                  <p className='text-sm font-medium text-foreground'>
                    {colorTemplates[templateKey].name}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {selectedTemplate === 'custom' && (
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
            {colorFields.map(field => (
              <div key={field.name} className='space-y-1.5'>
                <label className='block text-sm font-medium text-foreground'>{field.label}</label>
                <div className='flex items-center gap-2'>
                  <button
                    type='button'
                    onClick={() => setShowPicker(showPicker === field.name ? null : field.name)}
                    className='h-9 w-9 shrink-0 rounded border border-border'
                    style={{ backgroundColor: colorState[field.name] }}
                    aria-label={`Pick color for ${field.label}`}
                  />
                  <Input
                    type='text'
                    value={colorState[field.name]}
                    onChange={e =>
                      setColorState(prev => ({
                        ...prev,
                        [field.name]: e.target.value,
                      }))
                    }
                  />
                </div>
                {showPicker === field.name && (
                  <div style={{ position: 'absolute', zIndex: 2 }}>
                    <div
                      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }}
                      onClick={() => setShowPicker(null)}
                    />
                    <ChromePicker
                      color={colorState[field.name]}
                      onChange={color => handleColorChange(color, field.name)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <Button type='submit' className='mt-4'>
          {t('auth.update')}
        </Button>
      </form>

      <div className='mt-4'>
        <h5 className='text-foreground font-medium mb-3'>{t('colors.preview')}</h5>
        <div
          className='p-3 rounded-lg border border-border'
          style={{
            backgroundColor: colorState.backgroundColor,
          }}
        >
          <div
            className='p-3 rounded mb-3'
            style={{
              backgroundColor: colorState.primaryColor,
              color: colorState.navFontColor,
            }}
          >
            {t('colors.previewHeader')}
          </div>
          <button
            type='button'
            className='rounded px-4 py-2 text-sm font-medium'
            style={{
              backgroundColor: colorState.secondaryColor,
              color: colorState.btnFontColor,
              border: 'none',
            }}
          >
            {t('colors.previewButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColorConfig;
